import { useState, useEffect, useCallback, useRef } from 'react';
import { sessionsV2Api, SessionV2, SessionsV2Filters } from '../../../api/sessionsV2Api';

interface UseSiteShiftsOptions {
  siteId: number;
  filters?: SessionsV2Filters;
  autoRefresh?: boolean;
  refreshInterval?: number;
  enabled?: boolean; // Ленивая загрузка - загружать только если enabled = true
}

/** Небольшая задержка при изменении фильтров, чтобы при быстрых обновлениях (выбор пользователя и т.п.) выполнялся один запрос с актуальными фильтрами */
const FILTER_DEBOUNCE_MS = 80;

export const useSiteShifts = ({ 
  siteId, 
  filters, 
  autoRefresh = false, 
  refreshInterval = 30000,
  enabled = true // По умолчанию загружаем, но можно отключить
}: UseSiteShiftsOptions) => {
  const [sessions, setSessions] = useState<SessionV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadSessions = useCallback(async () => {
    if (!siteId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Если даты не выбраны, загружаем последние 30 смен с сервера
      // При этом сохраняем другие фильтры (userId, typeOfWorkId)
      let effectiveFilters: SessionsV2Filters | undefined = filters ? { ...filters } : undefined;
      // typeOfWorkId === 0 — «Не указанный вид работы»: фильтруем на клиенте, в API не передаём
      if (effectiveFilters?.typeOfWorkId === 0) {
        const { typeOfWorkId, ...rest } = effectiveFilters;
        effectiveFilters = rest;
      }
      if (!filters || (!filters.startDate && !filters.endDate)) {
        // Не устанавливаем период, но ограничиваем до 30 смен на сервере
        effectiveFilters = {
          ...effectiveFilters,
          startDate: undefined,
          endDate: undefined,
          limit: 30
        };
      }
      const data = await sessionsV2Api.getSiteSessions(siteId, effectiveFilters);
      
      setSessions(data);
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error('Error loading site sessions:', err);
      setError(err.response?.data || err.message || 'Ошибка загрузки смен');
    } finally {
      setLoading(false);
    }
  }, [siteId, filters]); // Фильтры в зависимостях - при их изменении функция пересоздается

  // Автоматическое обновление
  useEffect(() => {
    if (autoRefresh && refreshInterval > 0 && enabled) {
      const interval = setInterval(() => {
        loadSessions();
      }, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, loadSessions, enabled]);

  // При закрытии карточки очищаем данные, чтобы при следующем открытии не показывать старые
  useEffect(() => {
    if (!enabled) {
      setSessions([]);
      setError(null);
      setLastUpdated(null);
      setLoading(false);
    }
  }, [enabled]);

  // Загрузка при включении (enabled) или изменении фильтров — с дебаунсом, чтобы при выборе пользователя и закрытии BottomSheet выполнялся один запрос с актуальным userId
  useEffect(() => {
    if (!enabled) return;

    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }

    loadTimeoutRef.current = setTimeout(() => {
      loadTimeoutRef.current = null;
      loadSessions();
    }, FILTER_DEBOUNCE_MS);

    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
    };
  }, [enabled, filters, loadSessions]);

  const refresh = useCallback(() => {
    loadSessions();
  }, [loadSessions]);

  // Статистика
  const statistics = {
    totalSessions: sessions.length,
    totalHours: sessions.reduce((sum, session) => {
      const hours = session.hours || 0;
      return sum + hours;
    }, 0),
    totalAmount: sessions.reduce((sum, session) => sum + (session.totalAmount || 0), 0),
    paidAmount: sessions.reduce((sum, session) => sum + (session.paidAmount || 0), 0),
    bonusPaidAmount: sessions.reduce((sum, session) => sum + (session.bonusPaidAmount || 0), 0),
    remainingAmount: sessions.reduce((sum, session) => sum + (session.remainingAmount || 0), 0),
    hasFinancialAccess: sessions.some(session => session.hasFinancialAccess),
    uniqueUsers: new Set(sessions.map(session => session.userId)).size,
    uniqueTypeOfWorks: new Set(sessions.map(session => session.typeOfWorks)).size
  };

  return {
    sessions,
    loading,
    error,
    lastUpdated,
    statistics,
    refresh
  };
};
